import { ActionType, onLoggedOut } from "@src/app/store/session";
import { exchangeCognitoTokenForFirebase } from "@src/appV2/Auth/cognito/tokenExchange";
import {
  clearEmbeddedAuth,
  storeEmbeddedAuth,
  waitForTokenRestoration,
} from "@src/appV2/EmbeddedMode/parentTokenRelay";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import type { CognitoUser } from "@src/lib/cognito/src/lib/amplifyAuth";
import { getCognitoLocalStorageState } from "@src/lib/cognito/src/lib/cognitoLocalStorageInspector";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

export function useIsAuthenticated(): boolean {
  const dispatch = useDispatch();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const queryClient = useQueryClient();

  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });
  const isCognitoFailClosed = useCbhFlag(CbhFeatureFlag.COGNITO_FAIL_CLOSED, {
    defaultValue: false,
  });

  // Refs to avoid stale closures — values are read inside listener callbacks
  // but should not cause re-registration of listeners.
  const isDebuggingEnabledRef = useRef(isDebuggingEnabled);
  isDebuggingEnabledRef.current = isDebuggingEnabled;
  const isCognitoFailClosedRef = useRef(isCognitoFailClosed);
  isCognitoFailClosedRef.current = isCognitoFailClosed;

  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isCancelled = false;

    const cognitoAuth = getCognitoSingleton();
    const firebaseAuth = getFirebaseSingleton();

    logEvent(APP_V2_APP_EVENTS.COGNITO_STARTUP_LOCALSTORAGE_STATE, getCognitoLocalStorageState());

    // --- Cognito listener: bridges Cognito → Firebase, never sets isAuthenticated ---
    const handleCognitoTokenChanged = async (
      cognitoUser: CognitoUser | undefined
    ): Promise<void> => {
      if (isCancelled || !cognitoUser) {
        return;
      }

      try {
        // Wait for token restoration from parent window (Safari ITP workaround).
        // This ensures tokens are restored to localStorage before we check Cognito.
        await waitForTokenRestoration();

        if (cognitoUser.cbhUserId) {
          // Completed onboarding — only exchange if Firebase has no session
          logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORED, {
            uid: cognitoUser.uid,
            cbhUserId: cognitoUser.cbhUserId,
          });

          if (!firebaseAuth.currentUser) {
            const firebaseCustomToken = await exchangeCognitoTokenForFirebase();
            await firebaseAuth.signInWithCustomToken(firebaseCustomToken);
            // Firebase listener will fire → setAuthenticated(true)
          }

          return;
        }

        // Mid-onboarding — exchange token and set Redux state
        logEvent(APP_V2_APP_EVENTS.COGNITO_ONBOARDING_SESSION_RESTORED, {
          uid: cognitoUser.uid,
          phoneNumber: cognitoUser.phoneNumber,
        });

        const firebaseCustomToken = await exchangeCognitoTokenForFirebase();
        await firebaseAuth.signInWithCustomToken(firebaseCustomToken);

        if (!isCancelled) {
          dispatch({
            type: ActionType.SET_IS_SIGNUP,
            data: { isSignUp: true },
          });

          dispatch({
            type: ActionType.LOGGED_IN,
            data: {
              profile: { phone: cognitoUser.phoneNumber },
            },
          });
        }
      } catch (error) {
        if (isCognitoFailClosedRef.current) {
          logError(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORE_ERROR, {
            error,
            metadata: { context: "handleCognitoTokenChanged", cognitoFailClosed: true },
          });
        }
        // Firebase listener will handle auth state
      }
    };

    // --- Firebase listener: single source of truth for isAuthenticated ---
    const handleFirebaseTokenChanged = async (user: User | null): Promise<void> => {
      if (isCancelled) {
        return;
      }

      try {
        logEvent(APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED, {
          isUserAlreadyAuthenticated: isAuthenticatedRef.current,
          firebaseEmail: user?.email,
          userLastSignInTime: user?.metadata?.lastSignInTime,
        });

        if (user) {
          setAuthenticated(true);
          logEvent(APP_V2_APP_EVENTS.SIGNED_IN_TO_FIREBASE);

          // Store auth in parent window for embedded mode (Safari ITP workaround)
          storeEmbeddedAuth();

          await invalidateGetWorkerQuery(queryClient);
        } else {
          // Check if there's an active Cognito onboarding session before logging out
          try {
            const cognitoUser = await cognitoAuth.getCurrentUserInfo();

            if (cognitoUser && !cognitoUser.cbhUserId) {
              logEvent(APP_V2_APP_EVENTS.FIREBASE_LOGOUT_SKIPPED_FOR_COGNITO_ONBOARDING);
              return;
            }
          } catch (error) {
            if (isCognitoFailClosedRef.current) {
              logError(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORE_ERROR, {
                error,
                metadata: { context: "getCurrentUserInfoOnLogout", cognitoFailClosed: true },
              });
            }
            // If Cognito check fails, proceed with normal logout flow
          }

          logoutTimeoutRef.current = setTimeout(async () => {
            if (isCancelled) {
              return;
            }

            logEvent(APP_V2_APP_EVENTS.SIGNED_OUT_FROM_FIREBASE);
            setAuthenticated(false);

            await queryClient.cancelQueries([GET_WORKER_QUERY_KEY]);
            queryClient.removeQueries([GET_WORKER_QUERY_KEY]);

            // Clear auth from parent window for embedded mode (Safari ITP workaround)
            clearEmbeddedAuth();

            onLoggedOut(dispatch);
          }, 1000);
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_ON_HANDLE_TOKEN_CHANGE_ERROR, {
          error,
        });

        if (isDebuggingEnabledRef.current) {
          onLoggedOut(dispatch);
        }
      }
    };

    // Subscribe to Cognito auth changes
    const unsubscribeCognito = cognitoAuth.onIdTokenChanged(handleCognitoTokenChanged, (error) => {
      logError(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_ERROR, {
        error,
        metadata: { source: "cognitoListener" },
      });
    });

    // Subscribe to Firebase auth changes
    let unsubscribeFirebase: () => void = () => {};
    try {
      unsubscribeFirebase = firebaseAuth.onIdTokenChanged(handleFirebaseTokenChanged, (error) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_ERROR, { error });
      });
      logEvent(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_LISTENER_ADDED);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_HANDLER_ATTACHMENT_ERROR, {
        error,
      });
    }

    return () => {
      isCancelled = true;
      unsubscribeCognito();
      unsubscribeFirebase();
      clearTimeout(logoutTimeoutRef.current);
    };
    // Single registration — refs handle changing values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isAuthenticated;
}
